import configuration from '@/store/configuration';
import { gatewayInterceptor } from '@/services/interceptors/AccountGatewayInterceptor';

/**
 * Posts multipart/form-data using the native fetch API instead of axios.
 *
 * This exists as a workaround for QuantumMetric (quantum-fordpro.js), whose
 * XMLHttpRequest wrapper corrupts multipart bodies on cross-origin requests
 * by serializing FormData as JSON. Using fetch bypasses the XHR path QM
 * patches, so the FormData reaches the server intact.
 *
 * URL routing is delegated to `gatewayInterceptor` to stay consistent with
 * how AccountApiProxyService handles tile-mode / IAM-BFF / standalone modes,
 * and any configured proxy headers are forwarded (except Content-Type, so the
 * browser can set the multipart boundary itself).
 *
 * On a non-2xx HTTP response, throws an Error with a `response` property of
 * `{ status, data }`. Network/CORS failures propagate the native fetch
 * TypeError unchanged. Success returns `{ data }`. Callers should treat this
 * like axios only for the success path and the HTTP-error path.
 */
export async function postFormData(path, formData) {
  const routedConfig = gatewayInterceptor({
    url: path,
    baseURL: configuration.state.accountApiProxyBaseUrl || '',
  });

  const proxyHeaders = configuration.state.accountApiProxyHeaders || {};
  const forwardedHeaders = {};
  for (const [key, value] of Object.entries(proxyHeaders)) {
    if (key.toLowerCase() !== 'content-type') {
      forwardedHeaders[key] = value;
    }
  }

  const response = await fetch(routedConfig.baseURL + routedConfig.url, {
    method: 'POST',
    body: formData,
    headers: forwardedHeaders,
    credentials: configuration.state.accountApiProxyWithConfiguration
      ? 'include'
      : 'same-origin',
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new Error(
      `Request failed with status code ${response.status}`
    );
    error.response = { status: response.status, data };
    throw error;
  }

  return { data };
}
